import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { t } from 'i18next';

import { ArrowLeft, ArrowRightSmall, Xmark } from '@peacecover/icon';
import { SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { QUERY_KEYS } from '../image-search/constants/queryKeys';
import type { ImageSearchResponseType } from '../image-search/types/type';
import PhotoCardPost from '../components/card/PhotoCardPost';
import { formatPhotoCardAsTradingCard } from '../image-search/utils/formatPhotoCardAsTradingCard';
import { useSelectedTradingCardsStore } from './store/useSelectedTradingCardsStore';
import { getPhotoCardBadges } from './utils/getPhotoCardBadges';
import useUserQuery from '../hooks/queries/useUserQuery';
import BottomCTA from './components/BottomCTA';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { queryGet } from '@pocapan/shared/utils/queryClient';

const SELL_SIMILARITY_THRESHOLD = 0.8;

const SetSalePricePage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const showSuccessToast = useSuccessPhotoCardToast();
  const queryClient = useQueryClient();
  const { addPhotoCard } = useSelectedTradingCardsStore();
  const { data: user } = useUserQuery();

  const searchResult = queryClient.getQueryData<ImageSearchResponseType>(
    QUERY_KEYS.IMAGE_SEARCH_RESULT
  );

  const [prices, setPrices] = useState<Record<number, string>>({});
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [reportingIndex, setReportingIndex] = useState<number | null>(null);

  const selectedMember = JSON.parse(sessionStorage.getItem('sell-mode-selected-member') ?? 'null');

  useEffect(() => {
    if (!searchResult) {
      navigate(-1);
    }
  }, [searchResult, navigate]);

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'reported-new-photo-cards') return;

      const { photoCardId } = event.data.params;
      const res = await queryGet(`/api/v1/photo_cards/${photoCardId}`);
      const newPhotoCard = res?.photoCard;

      if (reportingIndex === null || !newPhotoCard) return;

      queryClient.setQueryData<ImageSearchResponseType>(
        QUERY_KEYS.IMAGE_SEARCH_RESULT,
        (old) => {
          if (!old) return old as unknown as ImageSearchResponseType;
          const updatedResults = old.results.map((result, i) =>
            i === reportingIndex
              ? { ...result, candidates: [{ ...result.candidates[0], photoCard: newPhotoCard, similarity: 1 }] as typeof result.candidates }
              : result
          );
          return { ...old, results: updatedResults };
        }
      );
      setReportingIndex(null);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [reportingIndex, queryClient]);

  if (!searchResult || !user) return null;

  const handlePriceChange = (index: number, value: string | undefined) => {
    setPrices((prev) => ({ ...prev, [index]: value || '' }));
  };

  const handleNext = () => {
    let hasExcluded = false;

    for (let i = 0; i < searchResult.results.length; i++) {
      if (excludedIndices.has(i)) continue;
      const result = searchResult.results[i];
      const candidate = result.candidates[0];
      const photoCard =
        (candidate?.similarity ?? 0) >= SELL_SIMILARITY_THRESHOLD
          ? candidate?.photoCard
          : undefined;
      const price = prices[i];

      if (!photoCard) {
        hasExcluded = true;
        continue;
      }

      addPhotoCard({ ...formatPhotoCardAsTradingCard(photoCard, user), price: price ?? null });
    }

    if (hasExcluded) {
      showSuccessToast('bottom', t('SetSalePricePage.excludedToast'), true);
    }

    navigate('/new-my-trading-photo-cards/sell');
  };

  return (
    <main className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))]">
      <header className="fixed top-0 left-0 right-0 z-10 mx-auto flex max-w-[var(--chakra-sizes-xl)] items-center bg-pure-white px-[16px] pb-[15px] pt-[calc(env(safe-area-inset-top)+8px)] dark:bg-background-dark">
        <ArrowLeft className="text-text-main-lv1" onClick={handleHistoryBack} />
      </header>
      <div className="mt-[69px] px-[24px] pb-[120px] flex flex-col">
        <div className="flex flex-col items-start gap-[14px] py-[10px]">
          <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1">
            {t('NewTradingPhotoCards.registerSalePhotoCardsPageTitle')}
          </h1>
          <p className="text-[15px] font-medium leading-[18px] text-text-main-lv4">
            {t('NewTradingPhotoCards.registerSalePhotoCardsPageDescription')}
          </p>
        </div>
        <div className="flex flex-col gap-[16px] mt-[8px]">
          {searchResult.results.map((result, index) => {
            if (excludedIndices.has(index)) return null;

            const candidate = result.candidates[0];
            const photoCard =
              (candidate?.similarity ?? 0) >= SELL_SIMILARITY_THRESHOLD
                ? candidate?.photoCard
                : undefined;

            if (photoCard) {
              const title = t('NewTradingPhotoCards.photoCardTitle', {
                artist: photoCard.memberName,
                category: photoCard.categoryName,
                categoryEmoji: photoCard.categoryEmoji
              });
              return (
                <PhotoCardPost
                  key={index}
                  title={title}
                  subtitle={photoCard.eventName}
                  imageUrl={photoCard.pictureUrl}
                  showInput={true}
                  showClose={true}
                  badges={getPhotoCardBadges(photoCard)}
                  inputValue={prices[index] ?? ''}
                  onInputChange={(value) => handlePriceChange(index, value)}
                  onClose={() => setExcludedIndices((prev) => new Set([...prev, index]))}
                />
              );
            }

            return (
              <UnregisteredPhotoCard
                key={candidate?.photoCardId ?? `unregistered-${index}`}
                imageUrl={result.originalUrl}
                onReport={() => {
                  setReportingIndex(index);
                  sessionStorage.setItem('report-prefilled-image', result.originalUrl ?? '');
                  openNewTab(
                    `/report-photo-card/event-activities?newTabOpen=true&artistId=${selectedMember?.artistId ?? ''}&memberId=${selectedMember?.memberId ?? ''}`
                  );
                }}
                onClose={() => setExcludedIndices((prev) => new Set([...prev, index]))}
              />
            );
          })}
        </div>
      </div>

      <BottomCTA className="mx-auto max-w-[var(--chakra-sizes-xl)]" onClick={handleNext}>
        {t('next')}
      </BottomCTA>
    </main>
  );
};

const UnregisteredPhotoCard = ({
  imageUrl,
  onReport,
  onClose
}: {
  imageUrl?: string;
  onReport: () => void;
  onClose: () => void;
}) => (
  <div className="relative w-full bg-background-light dark:bg-background-elevated rounded-[12px] shadow-[inset_0_0_0_1px_var(--color-separator1)] pl-[16px] pr-[10px] py-[16px] flex flex-col">
    <div className="flex flex-row gap-[14px] items-center">
      <ThumbnailPhotoCard imageUrl={imageUrl ?? ''} className="w-[48px] h-[75px]" />
      <button
        type="button"
        className="absolute top-[10px] right-[10px] p-[6px] flex items-center justify-center bg-secondary-light dark:bg-secondary-dark rounded-full"
        onClick={onClose}
      >
        <Xmark size={16} className="text-on-secondary" />
      </button>
      <div className="flex flex-col gap-[7px] leading-none">
        <p className="text-[15px] font-semibold text-text-main-lv1">{t('SetSalePricePage.unregisteredTitle')}</p>
        <p className="text-[13px] font-medium text-text-main-lv3">
          {t('SetSalePricePage.unregisteredDescription')}
        </p>
        <SquareBadge
          colorVariant="secondary"
          onClick={onReport}
          className="pl-[8px] pr-[3px] py-[3px]"
        >
          {t('SetSalePricePage.unregisteredButton')}
          <ArrowRightSmall size={12} className="ml-[2px]" />
        </SquareBadge>
      </div>
    </div>
    <div className="w-full h-px bg-separator1 mt-[14px] mb-[12px]" />
    <div className="flex flex-row items-center w-full pr-[6px]">
      <span className="text-[17px] font-medium text-placeholder-light dark:text-placeholder-dark min-w-fit mr-[10px] leading-none">
        {t('NewTradingPhotoCards.inputPricePlaceholder')}
      </span>
      <span className="ml-auto text-[17px] font-medium text-placeholder-light dark:text-placeholder-dark leading-none">
        {t('NewTradingPhotoCards.inputPriceUnit')}
      </span>
    </div>
  </div>
);

export default SetSalePricePage;
